import React from "react";
//#region redux
import { useSelector } from "react-redux";
//#endregion
//#region types
import { DimensionTypeItemMultiple } from "models/propTypes/dimensionPropTypes";
import { TranslationKeys } from "translations";
import { DimensionMultipleInputSelectorExtraProps, FormFieldProps } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import useMountedState from "../../useMountedState";
import DimensionsAutoCompleteInput from "../baseFields/dimensionSelector/autoCompleteInput";
import ReadOnlyField from "../baseFields/readOnlyField";
import { FormControl, Grid } from "@mui/material";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { useFormikContext } from "formik";

function DimensionMultipleInputSelectorFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, touched, disabled, readonly } = props;
  const { getMounted } = useMountedState();
  const [dimensionMap, setDimensionMap] = React.useState<DimensionTypeItemMultiple<AutocompleteListItem>[]>([]);
  const formikContext = useFormikContext<T>();

  const { type, allowDefaultDimensions, onSelectionChange, allowedTypes } =
    field.extraProps as DimensionMultipleInputSelectorExtraProps;

  const { dimensionTypes, dimensionSuggestions, dimensions } = useSelector(
    DimensionHelper.getAppStateDimensionSelector(type)
  );

  React.useEffect(() => {
    getMounted() && setDimensionMap(getDimensionTypeMap());
  }, [dimensionTypes, dimensions, dimensionSuggestions, value]);

  const getDimensionTypeMap = (): DimensionTypeItemMultiple<AutocompleteListItem>[] => {
    const selectedDimensionIds = props.value ?? [];
    let selectedUpdated = false;

    const allowedDimensionTypes = allowedTypes?.length
      ? dimensionTypes.filter((d) => allowedTypes.includes(d.type))
      : dimensionTypes;

    const dimensionsMap = allowedDimensionTypes.map(
      (companyDimType): DimensionTypeItemMultiple<AutocompleteListItem> => {
        const all = dimensions.find((d) => d.key.id === companyDimType.id);
        const suggestions = dimensionSuggestions.find((ds) => ds.key.id === companyDimType.id)?.value ?? [];
        const type = all?.key ?? companyDimType;

        let selected = all
          ? all.value
              .filter((item) => item.id && selectedDimensionIds.includes(item.id))
              .map((d): AutocompleteListItem => {
                return { id: d.id, value: d.name ?? "" };
              })
          : [];

        if (
          (!dimensionMap || dimensionMap.length === 0) &&
          allowDefaultDimensions &&
          !touched &&
          name &&
          selected === undefined &&
          !!type.recommendedDimensionId
        ) {
          selectedUpdated = true;
          const item = all?.value.find((item) => item.id === type.recommendedDimensionId);
          if (item) {
            selected = [{ id: item.id, value: item.name ?? "" }];
          }
        }

        return {
          id: type.id ?? "",
          name: type.name ?? "",
          type: type.type,
          suggestions: suggestions.map((dim): AutocompleteListItem => ({ id: dim.id, value: dim.name ?? "" })),
          selectedDimensions: selected,
          all:
            all?.value.map(
              (dim): AutocompleteListItem => ({
                id: dim.id,
                value: dim.name ?? "",
                group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Other),
              })
            ) ?? [],
        };
      }
    );

    if (selectedUpdated) raiseOnChangeEvent(dimensionsMap);
    return dimensionsMap;
  };

  const onDimensionSelect = React.useCallback(
    (dimensionTypeId: string, selectedDimensions: AutocompleteListItem[]) => {
      const dimensionsNew = [...dimensionMap];
      const dimension = dimensionsNew.find((x) => x.id === dimensionTypeId);
      if (dimension) {
        dimension.selectedDimensions =
          dimension && selectedDimensions && dimension.all.filter((d) => selectedDimensions.find((x) => x.id === d.id));

        onSelectionChange && onSelectionChange(selectedDimensions, dimensionTypeId, name, formikContext);
        raiseOnChangeEvent(dimensionsNew);
      }
    },
    [dimensionMap]
  );

  const raiseOnChangeEvent = (dimensionsUpdated: DimensionTypeItemMultiple<AutocompleteListItem>[]) => {
    props.handleOnChangeCustom(
      name,
      dimensionsUpdated
        .filter((x) => x.selectedDimensions !== undefined)
        .flatMap((dim) => dim.selectedDimensions.flatMap((d) => d.id)) as string[]
    );
  };

  const getValue = () => {
    return dimensionMap
      .flatMap((dim) => dim.selectedDimensions.map((d) => d.value))
      .filter((x) => !!x)
      .join(", ");
  };

  const getMultipleAutoCompleteInput = (dimensionType: DimensionTypeItemMultiple<AutocompleteListItem>) => {
    return (
      <DimensionsAutoCompleteInput
        dimensionType={dimensionType}
        onItemsSelect={onDimensionSelect.bind(null, dimensionType.id)}
        disabled={disabled}
        error={
          typeof props.errors !== "string" || props.errors.search(dimensionType.name) !== -1 ? props.errors : undefined
        }
      />
    );
  };

  return (
    <>
      {!readonly ? (
        <Grid container spacing={1}>
          {dimensionMap.map((dimension: DimensionTypeItemMultiple<AutocompleteListItem>, index) => {
            return props.layout ? (
              <props.layout.type
                key={dimension.id}
                {...props.layout.props}
                label={DimensionHelper.getDimensionTypeText(dimension.type, dimension.name)}
              >
                <FormControl fullWidth className={cssExports.inputFormControl}>
                  {getMultipleAutoCompleteInput(dimension)}
                </FormControl>
              </props.layout.type>
            ) : (
              <Grid key={index} item xs={6}>
                {getMultipleAutoCompleteInput(dimension)}
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <ReadOnlyField href={field.href}>{getValue()}</ReadOnlyField>
      )}
    </>
  );
}

export default DimensionMultipleInputSelectorFormField;
